import { Injectable } from '@angular/core';
import { AbstractSecurityStorage, LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, Observable, of, switchMap, take } from 'rxjs';

/**
 * Ключи библиотеки входа, которые описывают вход или обновление токена, идущие в конкретной вкладке:
 * state, nonce, code_verifier (PKCE), признаки «идёт обновление токена» и «идёт обмен кода», параметры запросов.
 */
const TAB_KEYS: ReadonlySet<string> = new Set([
    'authStateControl',
    'authNonce',
    'codeVerifier',
    'storageCodeFlowInProgress',
    'storageSilentRenewRunning',
    'storageCustomParamsAuthRequest',
    'storageCustomParamsRefresh',
    'storageCustomParamsEndSession',
]);

type StoredConfig = Record<string, unknown>;

/**
 * Хранилище angular-auth-oidc-client (#29300, как в Checkup — #29263): токены, данные пользователя и адреса
 * Keycloak — в localStorage, общем для вкладок (новая вкладка входит сразу, выход виден во всех), а вход
 * и обновление токена, которые идут во вкладке, — в её sessionStorage.
 *
 * Почему не DefaultLocalStorageService: библиотека сверяет state ответа Keycloak с сохранённым и после проверки
 * стирает его, а при обновлении токена заводит state заново. В общем localStorage вкладки, обновлявшие токен
 * одновременно, мешали друг другу: одна видела чужой или уже стёртый state («authCallback incorrect state»),
 * и библиотека удаляла токены — вход пропадал во всех вкладках. Так же при входе сразу в нескольких вкладках
 * одна перезаписывала другой state и code_verifier, и обмен кода получал 400.
 *
 * Библиотека читает и пишет всю запись конфигурации целиком (ключ — configId, значение — JSON).
 * Здесь запись делится по ключам. Общую часть пишем, только если библиотека её изменила: признак
 * «идёт обновление» она ставит и снимает раз в несколько секунд в каждой вкладке, и без этой проверки
 * каждая такая запись переписывала бы общие токены тем, что вкладка прочитала до этого.
 */
@Injectable()
export class TabAwareSecurityStorage implements AbstractSecurityStorage {

    /** Общая часть записи, какой эта вкладка прочитала её последний раз. */
    private readonly sharedAsRead = new Map<string, string>();
    private readonly usedKeys = new Set<string>();

    read(key: string): string | null {
        this.usedKeys.add(key);
        const shared = pick(parse(localStorage.getItem(key)), name => !TAB_KEYS.has(name));
        const tab = pick(parse(sessionStorage.getItem(key)), name => TAB_KEYS.has(name));
        this.sharedAsRead.set(key, JSON.stringify(shared));

        const stored = {...shared, ...tab};
        return Object.keys(stored).length ? JSON.stringify(stored) : null;
    }

    write(key: string, value: string): void {
        this.usedKeys.add(key);
        const stored = parse(value);
        const shared = JSON.stringify(pick(stored, name => !TAB_KEYS.has(name)));
        if (shared !== this.sharedAsRead.get(key)) {
            localStorage.setItem(key, shared);
            this.sharedAsRead.set(key, shared);
        }
        sessionStorage.setItem(key, JSON.stringify(pick(stored, name => TAB_KEYS.has(name))));
    }

    remove(key: string): void {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
        this.sharedAsRead.delete(key);
    }

    /** Только записи библиотеки: остальное в хранилищах (фильтры, настройки разделов) не трогаем. */
    clear(): void {
        this.usedKeys.forEach(key => this.remove(key));
    }
}

/** Адрес — возврат с Keycloak после входа: в параметрах state и код или ошибка. */
export function isAuthCallbackUrl(): boolean {
    const params = new URLSearchParams(window.location.search);
    return isCallbackUrl(new URLSearchParams(window.location.search));
}

/** То же для адреса, с которым работает гард: там параметры ещё не разобраны роутером. */
export function isCallbackUrl(query: URLSearchParams | string): boolean {
    const params =
        typeof query === 'string'
            ? new URLSearchParams(query.includes('?') ? query.slice(query.indexOf('?') + 1) : '')
            : query;

    return params.has('state') && (params.has('code') || params.has('error'));
}

/**
 * Проверка входа при старте приложения (вместо withAppInitializerAuthCheck). Есть refresh-токен и это не возврат
 * с Keycloak — checkAuthIncludingServer(): истёкший access-токен (вкладок сервиса давно не было, браузер
 * перезапускали) обновляется по refresh-токену без перехода на Keycloak, вход живёт, пока жива сессия Keycloak.
 * При возврате с Keycloak только разбираем ответ — прежним refresh-токеном не входим. Не вышло — продолжаем
 * без входа: защищённые разделы уведут на Keycloak.
 */
export function checkAuthOnStart(oidc: OidcSecurityService): Observable<LoginResponse | null> {
    return oidc.getRefreshToken().pipe(
        take(1),
        switchMap(refreshToken => refreshToken && !isAuthCallbackUrl()
            ? oidc.checkAuthIncludingServer()
            : oidc.checkAuth()),
        catchError(error => {
            console.warn('Проверка входа при старте не удалась, продолжаем без входа:', error);
            return of(null);
        }),
    );
}

function parse(json: string | null): StoredConfig {
    if (!json) {
        return {};
    }

    try {
        const value = JSON.parse(json);
        return value && typeof value === 'object' ? value as StoredConfig : {};
    } catch {
        return {};
    }
}

function pick(stored: StoredConfig, keep: (name: string) => boolean): StoredConfig {
    return Object.fromEntries(Object.entries(stored).filter(([name]) => keep(name)));
}
